import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import { useTranslation } from "react-i18next";
import moment from "moment";
import * as XLSX from "sheetjs-style";
import { Bar } from "react-chartjs-2";
import { canAction } from "../common/permissionhelper";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
);

export const EmailEffectivenessReport = ({}) => {
  const { t } = useTranslation();
  const emailTypeMap = {
    abandoned_cart: t("abandoned_cart_txt"),
    logged_in_not_order: t("logged_in_ordered_email_txt"),
    logged_in_not_order_cutoff: t("logged_in_ordered_email_cutoff_txt"),
  };
  const customStyles = {
    rows: { style: { minHeight: "25px" } },
    headCells: { style: { padding: "8px" } },
    cells: { style: { padding: "8px" } },
  };
  const paginationComponentOptions = {
    rowsPerPageText: "",
    rangeSeparatorText: "",
    selectAllRowsItem: true,
    selectAllRowsItemText: "",
    noRowsPerPage: "",
  };
  const [isLoading, setIsLoading] = useState(true);
  const [selectedemailtype, setSelectedemailtype] = useState("abandoned_cart");
  const [campaignData, setCampaignData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportAlert, setShowExportAlert] = useState(false);
  const [showNoDataAlert, setShowNoDataAlert] = useState(false);
  const [searchCampaign, setSearchCampaign] = useState("");
  const reportTabs = useMemo(
    () => [
      { name: t("table_view_txt"), value: "table", icon: "fa fa-bullhorn" },
      { name: t("bar_view_txt"), value: "bar", icon: "fa fa-list" },
    ],
    [t],
  );
  const [activeTab, setActiveTab] = useState("table");
  const CHART_TYPES = {
    RECOVERY_PERCENT: "recovery_percent",
    REVENUE: "revenue",
    CUSTOMERS: "customers",
  };
  const [chartType, setChartType] = useState(CHART_TYPES.RECOVERY_PERCENT);
  const noDataPlugin = {
    id: "noDataPlugin",
    beforeDraw: (chart) => {
      const { datasets } = chart.data;
      const hasData = datasets.some((dataset) => dataset.data.length > 0);
      if (!hasData) {
        const ctx = chart.ctx;
        const { width, height } = chart;
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = "16px Arial";
        ctx.fillStyle = "#eb9f9d";
        ctx.fillText(t("no_data_filtered_search_txt"), width / 2, height / 2);
        ctx.restore();
      }
    },
  };

  useEffect(() => {
    ChartJS.register(noDataPlugin);
  }, []);

  const fetchCampaignDetails = () => {
    setIsLoading(true);
    const URL = BaseUrl + "admin/emailcampaigneffectivenesssreport";
    http
      .get(URL)
      .then((res) => {
        setCampaignData(res.data?.data || []);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchCampaignDetails();
  }, []);
  // filtered data based on type and search
  const filteredCampaignData = useMemo(() => {
    return campaignData.filter((item) => {
      const matchesType =
        !selectedemailtype || item.emailType === selectedemailtype;
      const matchesSearch =
        !searchCampaign ||
        item.emailCampaignName
          ?.toLowerCase()
          .includes(searchCampaign.toLowerCase());
      return matchesType && matchesSearch;
    });
  }, [campaignData, selectedemailtype, searchCampaign]);
  // function to check if row is valid for chart based on chart type
  const isValidForChart = (row, chartType) => {
    const customerRecoveryPercent =
      row.customerCount && row.recoveredCustomerCount
        ? (row.recoveredCustomerCount / row.customerCount) * 100
        : 0;
    const revenueRecoveryPercent =
      row.totalAbandonedRevenue && row.recoveredRevenue
        ? (row.recoveredRevenue / row.totalAbandonedRevenue) * 100
        : 0;
    switch (chartType) {
      // Recovery %
      case CHART_TYPES.RECOVERY_PERCENT:
        return customerRecoveryPercent > 0 || revenueRecoveryPercent > 0;
      // Revenue Recovery → recovered revenue MUST exist
      case CHART_TYPES.REVENUE:
        return (row.recoveredRevenue || 0) > 0;
      // Customer Recovery → recovered customers MUST exist
      case CHART_TYPES.CUSTOMERS:
        return (row.recoveredCustomerCount || 0) > 0;
      default:
        return true;
    }
  };
  // chart data preparation
  const chartData = useMemo(() => {
    const validCampaigns = filteredCampaignData.filter((row) =>
      isValidForChart(row, chartType),
    );
    return validCampaigns.map((row) => ({
      campaign: row.emailCampaignName,
      abandonedRevenue: row.totalAbandonedRevenue || 0,
      recoveredRevenue: row.recoveredRevenue || 0,
      targetedCustomers: row.customerCount || 0,
      recoveredCustomers: row.recoveredCustomerCount || 0,
      customerRecoveryPercent:
        row.customerCount && row.recoveredCustomerCount
          ? (row.recoveredCustomerCount / row.customerCount) * 100
          : 0,
      revenueRecoveryPercent:
        row.totalAbandonedRevenue && row.recoveredRevenue
          ? (row.recoveredRevenue / row.totalAbandonedRevenue) * 100
          : 0,
    }));
  }, [filteredCampaignData, chartType]);

  const barChartConfig = useMemo(() => {
    const labels = chartData.map((d) => d.campaign);
    if (chartType === CHART_TYPES.RECOVERY_PERCENT) {
      return {
        labels,
        datasets: [
          {
            label: t("customer_recovery_chart_txt"),
            data: chartData.map((d) => d.customerRecoveryPercent),
            barThickness: 28,
            maxBarThickness: 40,
            categoryPercentage: 0.85,
            barPercentage: 0.9,
            borderRadius: 4,
            backgroundColor: "#1b81bc",
          },
          {
            label: t("revenue_recovery_chart_txt"),
            data: chartData.map((d) => d.revenueRecoveryPercent),
            barThickness: 28,
            maxBarThickness: 40,
            categoryPercentage: 0.85,
            barPercentage: 0.9,
            borderRadius: 4,
            backgroundColor: "#70dbd8",
          },
        ],
      };
    }
    if (chartType === CHART_TYPES.REVENUE) {
      return {
        labels,
        datasets: [
          {
            label: t("total_abandoned_cart_value_currency_txt"),
            data: chartData.map((d) => d.abandonedRevenue),
            backgroundColor: "#1b81bc",
            barThickness: 28,
            maxBarThickness: 40,
            categoryPercentage: 0.85,
            barPercentage: 0.9,
            borderRadius: 4,
          },
          {
            label: t("total_recovered_revenue_currency_txt"),
            data: chartData.map((d) => d.recoveredRevenue),
            backgroundColor: "#70dbd8",
            barThickness: 28,
            maxBarThickness: 40,
            categoryPercentage: 0.85,
            barPercentage: 0.9,
            borderRadius: 4,
          },
        ],
      };
    }
    return {
      labels,
      datasets: [
        {
          label: t("targeted_customers_txt"),
          data: chartData.map((d) => d.targetedCustomers),
          backgroundColor: "#1b81bc",
          barThickness: 28,
          maxBarThickness: 40,
          categoryPercentage: 0.85,
          barPercentage: 0.9,
          borderRadius: 4,
        },
        {
          label: t("recovered_customers_txt"),
          data: chartData.map((d) => d.recoveredCustomers),
          backgroundColor: "#70dbd8",
          barThickness: 28,
          maxBarThickness: 40,
          categoryPercentage: 0.85,
          barPercentage: 0.9,
          borderRadius: 4,
        },
      ],
    };
  }, [chartData, chartType, t]);

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    scales: {
      y: {
        ticks: {
          autoSkip: false,
          color: "rgba(207, 208, 211, 1)",
        },
        grid: {
          color: "rgba(247, 243, 241, 0.8)",
        },
      },
      x: {
        beginAtZero: true,
        grid: {
          color: "rgba(247, 243, 241, 0.8)",
        },
        ticks: {
          color: "rgba(207, 208, 211, 1)",
          callback: function (value) {
            if (chartType === CHART_TYPES.RECOVERY_PERCENT) {
              return formatPercent(value);
            }
            if (chartType === CHART_TYPES.REVENUE) {
              return `£${value.toLocaleString()}`;
            }
            // CUSTOMERS
            return value.toLocaleString();
          },
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.raw;
            if (chartType === CHART_TYPES.RECOVERY_PERCENT) {
              return `${context.dataset.label}: ${formatPercent(value)}`;
            }
            if (chartType === CHART_TYPES.REVENUE) {
              return `${context.dataset.label}: £${value.toLocaleString(
                undefined,
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                },
              )}`;
            }
            return `${context.dataset.label}: ${value.toLocaleString()}`;
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
  };
  // base columns for all email types
  const baseColumns = [
    {
      name: t("campaign_namee_txt"),
      selector: (row) => row?.emailCampaignName,
      sortable: true,
    },
    {
      name: t("email_type_txt"),
      selector: (row) => emailTypeMap[row.emailType] || row.emailType,
      sortable: true,
    },
    {
      name: t("cutoff_time_txt"),
      selector: (row) => row?.cutoffTime,
      sortable: true,
    },
    {
      name: t("campaign_mode_status_txt"),
      selector: (row) => {
        const mode = row?.autoEmail ? t("auto_txt") : t("manual_txt");
        let status = "";
        if (row?.status) {
          const s = row.status.toLowerCase();
          const r = row.runStatus?.toLowerCase();
          if (s === "scheduled" && r === "run") status = t("running_txt");
          else if (s === "scheduled" && r === "pause") status = t("paused_txt");
          else if (s === "scheduled" && r === "stop")
            status = t("completed_txt");
          else status = s.charAt(0).toUpperCase() + s.slice(1);
        }
        return `${mode} / ${status}`;
      },
      sortable: true,
    },
    {
      name: t("total_targeted_customers_count_txt"),
      selector: (row) =>
        row?.customerCount && row.customerCount !== 0 ? row.customerCount : "-",
      sortable: true,
    },
    {
      name: t("total_recovered_customers_txt"),
      selector: (row) =>
        row?.recoveredCustomerCount && row.recoveredCustomerCount !== 0
          ? row.recoveredCustomerCount
          : "-",
      sortable: true,
    },
    {
      name: t("customer_recovery_chart_txt"),
      selector: (row) => {
        if (row?.customerCount && row?.recoveredCustomerCount) {
          const percent =
            (row.recoveredCustomerCount / row.customerCount) * 100;

          const rounded = Math.round(percent * 10) / 10;
          return rounded % 1 === 0 ? `${rounded.toFixed(0)}%` : `${rounded}%`;
        }
        return "-";
      },
      sortable: true,
    },
  ];
  // abandoned cart specific columns
  const abandonedCartColumns = [
    {
      name: t("total_abandoned_cart_value_txt"),
      selector: (row) =>
        row?.totalAbandonedRevenue && row.totalAbandonedRevenue !== 0
          ? `£${row.totalAbandonedRevenue.toFixed(2)}`
          : "-",
      sortable: true,
    },
    {
      name: t("total_recovered_revenue_txt"),
      selector: (row) =>
        row?.recoveredRevenue && row.recoveredRevenue !== 0
          ? `£${row.recoveredRevenue.toFixed(2)}`
          : "-",
      sortable: true,
    },
    {
      name: t("revenue_recovery_percentage_txt"),
      selector: (row) => {
        if (
          row?.totalAbandonedRevenue &&
          row.totalAbandonedRevenue !== 0 &&
          row?.recoveredRevenue
        ) {
          const percent =
            (row.recoveredRevenue / row.totalAbandonedRevenue) * 100;
          const rounded = Math.round(percent * 10) / 10;
          return rounded % 1 === 0 ? `${rounded.toFixed(0)}%` : `${rounded}%`;
        }
        return "-";
      },
      sortable: true,
    },
  ];
  // combined columns based on email type
  const campaignColumns = useMemo(() => {
    return selectedemailtype === "abandoned_cart"
      ? [...baseColumns, ...abandonedCartColumns]
      : baseColumns;
  }, [selectedemailtype]);
  // export to excel handlers
  const handleConfirmExportToExcel = () => {
    if (!filteredCampaignData || filteredCampaignData.length === 0) {
      setShowNoDataAlert(true);
      return;
    }
    setShowExportAlert(true);
  };

  const confirmExport = () => {
    setIsExporting(true);
    setShowExportAlert(false);
    handleExportToExcel();
  };
  // export to excel function
  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      const campaignDataForExport = [...filteredCampaignData];
      // 1. Email Type Label
      const typeLabel = selectedemailtype
        ? `${emailTypeMap[selectedemailtype]} ${t("campaign_recovery_details_txt")}`
        : t("all_email_type_campaign_recovery_details_txt");
      // 2. Search Label (Grouped by)
      const searchLabel = searchCampaign
        ? ` | ${t("grouped_by_txt")}: ${searchCampaign}`
        : "";
      // 3. Total Records
      const totalLabel = ` | ${t("total_records_txt")}: ${filteredCampaignData.length}`;
      // FINAL TITLE
      const finalTitle = `${typeLabel}${searchLabel}${totalLabel}`;
      const wb = XLSX.utils.book_new();
      const emptyRows = Array(5).fill([]);
      const noDataRow = [
        {
          v: t("no_records_available_txt"),
          s: {
            font: {
              bold: true,
              sz: 14,
              italic: true,
              color: { rgb: "FF0000" },
            },
            alignment: { horizontal: "center" },
          },
        },
      ];
      // Campaign Details Sheet
      const campaignHeaders =
        selectedemailtype === "abandoned_cart"
          ? [
              t("campaign_namee_txt"),
              t("email_type_txt"),
              t("cutoff_time_txt"),
              t("campaign_mode_status_txt"),
              t("total_targeted_customers_count_txt"),
              t("total_recovered_customers_txt"),
              t("customer_recovery_percentage_txt"),
              t("total_abandoned_cart_value_txt"),
              t("total_recovered_revenue_txt"),
              t("revenue_recovery_percentage_txt"),
            ]
          : [
              t("campaign_namee_txt"),
              t("email_type_txt"),
              t("cutoff_time_txt"),
              t("campaign_mode_status_txt"),
              t("total_targeted_customers_count_txt"),
              t("total_recovered_customers_txt"),
              t("customer_recovery_percentage_txt"),
            ];
      const campaignRows = campaignDataForExport.map((row) => {
        const mode = row?.autoEmail ? t("auto_txt") : t("manual_txt");
        let status = "";
        if (row?.status) {
          const s = row.status.toLowerCase();
          const r = row.runStatus?.toLowerCase();
          if (s === "scheduled" && r === "run") status = t("running_txt");
          else if (s === "scheduled" && r === "pause") status = t("paused_txt");
          else if (s === "scheduled" && r === "stop")
            status = t("completed_txt");
          else status = s.charAt(0).toUpperCase() + s.slice(1);
        }
        // Customer recovery %
        let customerRecovery = "-";
        if (row.customerCount && row.recoveredCustomerCount) {
          const percent =
            (row.recoveredCustomerCount / row.customerCount) * 100;
          customerRecovery = Number.isInteger(percent)
            ? `${percent}%`
            : `${percent.toFixed(1)}%`;
        }
        const baseRow = [
          row.emailCampaignName || "-",
          emailTypeMap[row.emailType] || row.emailType,
          row.cutoffTime || "-",
          `${mode} / ${status}`,
          row.customerCount || 0,
          row.recoveredCustomerCount || 0,
          customerRecovery,
        ];
        // Add revenue columns ONLY for abandoned cart
        if (selectedemailtype === "abandoned_cart") {
          let revenueRecovery = "-";
          if (row.totalAbandonedRevenue && row.recoveredRevenue) {
            const percent =
              (row.recoveredRevenue / row.totalAbandonedRevenue) * 100;
            revenueRecovery = Number.isInteger(percent)
              ? `${percent}%`
              : `${percent.toFixed(1)}%`;
          }
          baseRow.push(
            row.totalAbandonedRevenue
              ? `£${row.totalAbandonedRevenue.toFixed(2)}`
              : "£0.00",
            row.recoveredRevenue
              ? `£${row.recoveredRevenue.toFixed(2)}`
              : "£0.00",
            revenueRecovery,
          );
        }
        return baseRow;
      });
      const campaignSheetData =
        campaignDataForExport.length > 0
          ? [
              [
                {
                  v: finalTitle,
                  s: {
                    font: { bold: true },
                    alignment: { horizontal: "left" },
                  },
                },
              ],
              campaignHeaders.map((h) => ({
                v: h,
                s: { font: { bold: true }, alignment: { horizontal: "left" } },
              })),
              ...campaignRows,
            ]
          : [
              [
                {
                  v: finalTitle,
                  s: {
                    font: { bold: true },
                    alignment: { horizontal: "left" },
                  },
                },
              ],
              [],
              ...emptyRows,
              noDataRow,
            ];
      const wsCampaign = XLSX.utils.aoa_to_sheet(campaignSheetData);
      wsCampaign["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: campaignHeaders.length - 1 } },
      ];
      if (campaignDataForExport.length === 0) {
        wsCampaign["!merges"].push({
          s: { r: 2 + emptyRows.length, c: 0 },
          e: { r: 2 + emptyRows.length, c: campaignHeaders.length - 1 },
        });
      }
      wsCampaign["!cols"] = campaignHeaders.map((h, i) => {
        let maxLength = h.length;
        campaignRows.forEach((r) => {
          maxLength = Math.max(maxLength, String(r[i]).length);
        });
        return { wch: maxLength + 5 };
      });
      XLSX.utils.book_append_sheet(
        wb,
        wsCampaign,
        t("campaign_effectiveness_details_txt"),
      );
      // Save Excel
      const exportDate = moment().format("DD-MM-YYYY");
      XLSX.writeFile(wb, `EmailEffectivenessReport-${exportDate}.xlsx`);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const formatPercent = (value) => {
    if (Number.isInteger(value)) {
      return `${value}%`;
    }
    const rounded = Math.round(value * 100) / 100;
    return rounded % 1 === 0 ? `${rounded.toFixed(0)}%` : `${rounded}%`;
  };

  const isNonRevenueType =
    selectedemailtype === "logged_in_not_order" ||
    selectedemailtype === "logged_in_not_order_cutoff";

  useEffect(() => {
    if (isNonRevenueType) {
      setChartType(CHART_TYPES.CUSTOMERS);
    }
  }, [selectedemailtype]);

  return (
    <div
      className={`chart-container email-report-table ${
        isLoading ? "loading-buffer" : ""
      }`}
      style={{ verticalAlign: "top" }}
    >
      <div className="chart-Hdr">
        <h2>{t("campaign_recovery_report_txt")}</h2>
        <div className="chart-fltr">
          {activeTab === "table" && (
            <>
              {canAction("manage_dashboard.emailreport", "export") && (
                <div className="prod-import-btn odr-expo-btn">
                  <a
                    data-title={t("export_filtered_records")}
                    className="export-btn button title-tooltip title-left"
                    onClick={handleConfirmExportToExcel}
                  >
                    {t("export_btn_txt")}
                  </a>
                </div>
              )}
              <div className="chart-fltr" style={{ width: "200px" }}>
                <input
                  type="text"
                  value={searchCampaign}
                  onChange={(e) => setSearchCampaign(e.target.value)}
                  placeholder={t("search_campaign_name_txt")}
                />
              </div>
            </>
          )}
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
          >
            {reportTabs.map((tab) => (
              <option key={tab.value} value={tab.value}>
                {tab.name}
              </option>
            ))}
          </select>
          {/* Chart type dropdown ONLY for Summary tab */}
          {activeTab === "bar" && (
            <div className="chart-fltr">
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
              >
                {!isNonRevenueType && (
                  <>
                    <option value={CHART_TYPES.RECOVERY_PERCENT}>
                      {t("recovery_percentage_txt")}
                    </option>
                    <option value={CHART_TYPES.REVENUE}>
                      {" "}
                      {t("revenue_recovery_txt")}{" "}
                    </option>
                  </>
                )}
                <option value={CHART_TYPES.CUSTOMERS}>
                  {" "}
                  {t("customer_recovery_txt")}{" "}
                </option>
              </select>
            </div>
          )}
          <select
            value={selectedemailtype}
            onChange={(e) => setSelectedemailtype(e.target.value)}
          >
            {Object.entries(emailTypeMap).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        {activeTab === "table" && filteredCampaignData.length > 0 && (
          <div className="fltr-sub-val">
            <span className="fltr-val">
              {t("total_record(s)_txt")}{" "}
              <strong>{filteredCampaignData.length}</strong>
            </span>
          </div>
        )}
      </div>
      {activeTab === "table" && (
        <DataTable
          columns={campaignColumns}
          data={filteredCampaignData}
          customStyles={customStyles}
          noDataComponent={
            <div className="no-records-found">
              {t("no_data_filtered_search_txt")}
            </div>
          }
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 40, 60, 80, 100]}
          paginationComponentOptions={paginationComponentOptions}
        />
      )}
      {/* SUMMARY BAR CHART */}
      {activeTab === "bar" && (
        <div
          className="chart-container chart-revenue"
          style={{ height: "550px", width: "100%" }}
        >
          <Bar
            data={{
              labels: barChartConfig.labels || [],
              datasets:
                barChartConfig.datasets?.length > 0
                  ? barChartConfig.datasets
                  : [
                      {
                        label: "",
                        data: [],
                      },
                    ],
            }}
            options={barChartOptions}
            plugins={[noDataPlugin]}
          />
        </div>
      )}
      {isExporting && (
        <div className="loading-full-bg">
          <div className="loading-buffer"></div>
        </div>
      )}
      {showExportAlert && (
        <div className="custom-alert-pop paylater-popup">
          <div className="custom-alert-container">
            <div className="custom-alert-wrap">
              <div className="custom-alert-header">
                <h4>{t("alert_txt")}</h4>
              </div>
              <div className="custom-alert-body">
                <p>{t("export_confirm_txt")}</p>
                <div className="alert-buttons">
                  <a onClick={confirmExport} className="popup-alert-btn button">
                    {t("ok_txt")}
                  </a>
                  <a
                    onClick={() => setShowExportAlert(false)}
                    className="popup-alert-btn button"
                  >
                    {t("cancel_txt")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showNoDataAlert && (
        <div className="custom-alert-pop paylater-popup">
          <div className="custom-alert-container">
            <div className="custom-alert-wrap">
              <div className="custom-alert-header">
                <h4>{t("notice_txt")}</h4>
              </div>
              <div className="custom-alert-body">
                <p>{t("no_records_for_export_txt")}</p>
                <div className="alert-buttons">
                  <a
                    onClick={() => setShowNoDataAlert(false)}
                    className="popup-alert-btn button"
                  >
                    {t("ok_txt")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
